// CategoryIcons.jsx
import React, { useEffect, useState, useRef } from "react";
import { supabase } from "../../supabase/supabase";
import "./CategoryIcons.css";
import { useLanguage } from "../../contexts/LanguageContext"; // ⬅️ add this

// Helper function to resolve category logo URL from Supabase Storage
const resolveCategoryLogoUrl = (filename) => {
  if (!filename) return null;
  
  // If already a full URL, return it
  if (typeof filename === 'string' && /^(http|https):\/\//i.test(filename)) {
    return filename;
  }
  
  // If data URL, return it
  if (typeof filename === 'string' && filename.startsWith('data:')) {
    return filename;
  }
  
  // Construct URL from Supabase Storage categories bucket
  try {
    const { data } = supabase.storage.from('categories').getPublicUrl(filename);
    return data?.publicUrl || null;
  } catch (e) {
    console.log('[CategoryIcons] Error resolving logo URL:', e);
    return null;
  }
};

export default function CategoryIcons({ onCategoryClick, selectedCategoryId }) {
  const [categories, setCategories] = useState([]);
  const wrapperRef = useRef(null);
  const isDown = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  // ⬇️ detect current language (works with common field names)
  const { translations: tRaw, lang, language, locale } = useLanguage();
  const currentLang =
    (lang || language || locale || tRaw?.lang || tRaw?.locale || "en")
      .toString()
      .toLowerCase();
  const isArabic = currentLang.startsWith("ar");

  useEffect(() => {
    (async () => {
      try {
        // Fetch categories from account_categories table in Supabase
        const { data: categoriesData, error } = await supabase
          .from('account_categories')
          .select('*');

        if (error) {
          console.error('[CategoryIcons] Error fetching categories:', error);
          return;
        }

        // Transform and sort categories; use order column when present
        const transformedCategories = (categoriesData || [])
          .map(cat => {
            const logoFilename =
              cat.image_filename ||
              cat.logo ||
              cat.logo_filename ||
              cat.image ||
              `${cat.name}.png`;

            return {
              id: cat.id,
              name: cat.name,
              name_ar: cat.name_ar || cat.name,
              // keep original sort_order value (if exists) for sorting
              sort_order: typeof cat.sort_order === 'number' ? cat.sort_order : null,
              logo: resolveCategoryLogoUrl(logoFilename)
            };
          })
          .sort((a, b) => {
            const sortOrderA = a.sort_order ?? 0;
            const sortOrderB = b.sort_order ?? 0;
            if (sortOrderA !== sortOrderB) return sortOrderA - sortOrderB;
            return (a.name || '').localeCompare(b.name || '');
          });

        setCategories(transformedCategories);
      } catch (err) {
        console.error('[CategoryIcons] Error loading categories:', err);
      }
    })();
  }, []);

  const onMouseDown = e => {
    isDown.current = true;
    wrapperRef.current.classList.add("dragging");
    startX.current = e.pageX - wrapperRef.current.offsetLeft;
    scrollLeft.current = wrapperRef.current.scrollLeft;
  };
  const onMouseMove = e => {
    if (!isDown.current) return;
    e.preventDefault();
    const x = e.pageX - wrapperRef.current.offsetLeft;
    wrapperRef.current.scrollLeft = scrollLeft.current - (x - startX.current);
  };
  const onMouseUp = () => {
    isDown.current = false;
    wrapperRef.current.classList.remove("dragging");
  };
  const onMouseLeave = onMouseUp;
  const onTouchStart = e => {
    isDown.current = true;
    startX.current = e.touches[0].pageX - wrapperRef.current.offsetLeft;
    scrollLeft.current = wrapperRef.current.scrollLeft;
  };
  const onTouchMove = e => {
    if (!isDown.current) return;
    const x = e.touches[0].pageX - wrapperRef.current.offsetLeft;
    wrapperRef.current.scrollLeft = scrollLeft.current - (x - startX.current);
  };
  const onTouchEnd = onMouseUp;

  return (
    <div
      className="category-icons-wrapper"
      ref={wrapperRef}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {categories.map(cat => {
        const isSelected = selectedCategoryId === cat.id;
        const displayName = isArabic ? (cat.name_ar || cat.name) : cat.name; // ⬅️ pick AR name if available

        return (
          <div
            key={cat.id}
            className={`category-icon-item ${isSelected ? "selected" : ""}`}
            onClick={() => onCategoryClick(cat.id)}
            title={displayName} // optional tooltip in the right language
          >
            <img
              src={cat.logo || "/default-category.png"}
              alt={displayName}
              className="category-icon-image"
              onError={(e) => {
                e.target.src = "/default-category.png";
              }}
            />
            <div
              className={`category-icon-label ${isSelected ? "show" : ""}`}
              dir={isArabic ? "rtl" : "ltr"} // optional: make the label direction correct
            >
              {displayName}
            </div>
          </div>
        );
      })}
    </div>
  );
}
